import React from 'react';

import '../style.css';
import { Line, LinePath } from '@vx/shape';
import { Group } from '@vx/group';
import { Grid } from '@vx/grid';
import { scaleTime, scaleLinear } from '@vx/scale';
import { AxisBottom } from '@vx/axis';
import { extent, min, max } from 'd3-array';

import CONF from '../Conf';

export default function MetricPredictionsCard({
  predictionsForTemp,
  predictionsForHumid,
  predictionsForCo2,
  predictionsForVoc,
  predictionsForDust,
  predictionsForPm25,
  predictionsForPm10,
  abTest,
}) {
  let comp, data;
  switch (true) {
    case predictionsForTemp != null:
      comp = 'TEMP';
      data = predictionsForTemp;
      break;
    case predictionsForHumid != null:
      comp = 'HUMID';
      data = predictionsForHumid;
      break;
    case predictionsForCo2 != null:
      comp = 'CO2';
      data = predictionsForCo2;
      break;
    case predictionsForVoc != null:
      comp = 'VOC';
      data = predictionsForVoc;
      break;
    case predictionsForDust != null:
      comp = 'DUST';
      data = predictionsForDust;
      break;
    case predictionsForPm25 != null:
      comp = 'PM25';
      data = predictionsForPm25;
      break;
    case predictionsForPm10 != null:
      comp = 'PM10';
      data = predictionsForPm10;
      break;
    default:
      return null;
  }

  if (data.length === 0) {
    return null;
  }

  const predictedValues = data.map((i) => i.prediction);

  var peak, valley;

  // The logic is for rounding.
  switch (true) {
    case comp === 'CO2':
      peak = Math.round(Math.max(...predictedValues) / 100) * 100;
      valley = Math.round(Math.min(...predictedValues) / 100) * 100;
      if (peak === valley) {
        peak += 100;
      }
      break;
    case comp === 'VOC':
      const lowest = Math.min(...predictedValues);
      if (lowest >= 1000) {
        peak = Math.round(Math.max(...predictedValues) / 1000) * 1000;
        valley = Math.round(lowest / 1000) * 1000;
        if (peak === valley) {
          peak += 1000;
        }
      } else {
        peak = Math.round(Math.max(...predictedValues) / 100) * 100;
        valley = Math.round(lowest / 100) * 100;
        if (peak === valley) {
          peak += 100;
        }
      }
      break;
    case comp === 'HUMID':
      peak = Math.round(Math.max(...predictedValues));
      valley = Math.round(Math.min(...predictedValues));
      if (peak === valley) {
        if (peak === 100) {
          valley -= 1;
        } else {
          peak += 1;
        }
      }
    default:
      peak = Math.round(Math.max(...predictedValues));
      valley = Math.round(Math.min(...predictedValues));
      if (peak === valley) {
        peak += 1;
      }
      break;
  }

  // This is for temperature value change per temperature unit.
  if (comp === 'TEMP') {
    peak = CONF.temperature(peak).toFixed(1);
    valley = CONF.temperature(valley).toFixed(1);
  }

  const body = (
    <p>
      {CONF.text['Your (a) may be as high as (b) (c) and as low as (d) (e) tomorrow'](
        CONF.formattedDimensionKind(comp),
        peak,
        CONF.dimensionUnitFromKind(comp, false),
        valley,
        CONF.dimensionUnitFromKind(comp, false)
      )}
    </p>
  );

  const margin = {
    left: 10,
    right: 26,
    bottom: 34,
    top: 0,
  };

  let width = 160;
  const height = 100;

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const xScale = scaleTime({
    range: [0, xMax],
    domain: extent(data, (d) => new Date(d.time)),
  });

  const yScale = scaleLinear({
    range: [yMax, 0],
    domain: [min(data, (d) => d.prediction), max(data, (d) => d.prediction)],
    nice: true,
  });

  return (
    <article className='MetricPredictions Card' id={abTest}>
      <div>
        <header>
          {CONF.iconFromMetric(comp)}
          <h1>{CONF.formattedDimensionKind(comp)}</h1>
        </header>
        <section className='PredictedValuesWithPeaks'>
          {body}
          <svg viewBox='0 0 144 100' preserveAspectRatio='none'>
            <Grid
              top={margin.top}
              left={margin.left}
              width={xMax}
              height={yMax}
              stroke='var(--silver)'
              strokeWidth={'0.5px'}
              numTicksRows={0}
              numTicksColumns={6}
              xScale={xScale}
              yScale={yScale}
            />
            <Group top={margin.top} left={margin.left}>
              <LinePath
                data={data}
                xScale={xScale}
                yScale={yScale}
                x={(d) => new Date(d.time)}
                y={(d) => d.prediction}
                stroke={'var(--blue)'}
                strokeWidth={2}
              />
            </Group>
            <AxisBottom
              scale={xScale}
              top={height - margin.bottom}
              left={margin.left}
              tickStroke='var(--black)'
              stroke='var(--black)'
              numTicks={6}
              labelOffset={0}>
              {(props) => {
                const tickLabelSize = 4;
                const tickRotate = 0;
                const tickColor = 'var(--nickel)';
                const tickLabelColor = 'var(--grime)';
                const tickLabelOffset = 5;
                return (
                  <g>
                    {props.ticks.map((tick, i) => {
                      const tickX = tick.to.x;
                      const tickY = tick.to.y + tickLabelSize + props.tickLength - tickLabelOffset;
                      return (
                        <Group key={`vx-tick-${tick.value}-${i}`} className={'vx-axis-tick'}>
                          <Line from={tick.from} to={tick.to} stroke={tickColor} />
                          <text
                            transform={`translate(${tickX}, ${tickY}) rotate(-${tickRotate})`}
                            fontSize={tickLabelSize}
                            fontFamily={
                              "'SFProDisplay-Light', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI, Roboto, 'Helvetica Neue, 'Helvetica, Arial, sans-serif"
                            }
                            letterSpacing={'-0.3'}
                            textAnchor='middle'
                            fill={tickLabelColor}>
                            {tick.formattedValue}
                          </text>
                        </Group>
                      );
                    })}
                  </g>
                );
              }}
            </AxisBottom>
          </svg>
        </section>
      </div>
    </article>
  );
}
